import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card } from "@/design-system/components/ui";
import { formatCurrency } from "@/components/deals/dealFormat";
import { useAuth } from "@/hooks/useAuth";
import { useLoanIntakeBundle } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { LoanAdminFieldsCard } from "../profile/LoanAdminFieldsCard";
import { LoanTermsCard } from "../profile/LoanTermsCard";
import { BuyerConsultingFeeTermsCard } from "../profile/BuyerConsultingFeeTermsCard";
import { PaymentAdministrationFeeTermsCard } from "../profile/PaymentAdministrationFeeTermsCard";
import { FinancialHistoryCard } from "../profile/FinancialHistoryCard";
import { CovenantCalibrationCard } from "../profile/CovenantCalibrationCard";
import { SeasonalityReserveCard } from "../profile/SeasonalityReserveCard";
import { DscrDacaBankingCard } from "../profile/DscrDacaBankingCard";
import { IntakeSectionEditDrawer } from "../profile/IntakeSectionEditDrawer";
import {
  INTAKE_SECTIONS,
  sectionValues,
  type IntakeFieldConfig,
  type IntakeFieldValue,
  type IntakeSectionId,
} from "../profile/intakeSectionConfig";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import type { LoanIntakeProfileDto } from "@/types/intakeProfile";

// Deal Submission spec §6.1 + Loan record foundations + B6
// Profile editing — the loan Profile tab. Leads with the executed Loan
// terms (editable with reason, Loans.Manage), then the carry-forward
// sections (each captioned with its Deal provenance; per-section Edit in a
// right drawer, Loans.Update — values are LOAN-LOCAL, the Deal snapshot is
// never modified, and edited fields flip to "Edited post-close"), the
// financial history (add/edit periods), and the extensible "Loan
// Administration fields".

interface Props {
  loan: LoanSummaryDto;
}

export function LoanIntakeProfileTab({ loan }: Props) {
  const query = useLoanIntakeBundle(loan.id);
  const auth = useAuth();
  const canUpdate =
    auth.hasPermission("Loans.Update") || auth.hasRoleCode("ADMIN");

  const [editingSection, setEditingSection] = useState<IntakeSectionId | null>(null);

  if (query.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid m-0">Loading intake profile…</p>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0" role="alert">
          Couldn&apos;t load the intake profile. Please retry.
        </p>
      </Card>
    );
  }

  const bundle = query.data;
  const profile = bundle?.profile ?? null;
  const history = bundle?.financialHistory ?? [];

  const dealCaption = (
    <>
      Carried from{" "}
      <Link
        to={`/deal-flow/deals/${loan.dealId}`}
        className="text-gold-shadow hover:underline"
        data-testid="profile-deal-link"
      >
        Deal {loan.dealNumber ?? "record"}
      </Link>
    </>
  );

  return (
    <div className="flex flex-col gap-space-4">
      <LoanTermsCard loan={loan} />
      {/* Read-only, and a sibling rather than a block inside LoanTermsCard —
          the write is gated on Loans.ManageBuyerConsultingFee (ADMIN-only by
          absence from every role map) where that card's editor is Loans.Manage,
          and a Phoenix carve out of the seller's distribution is not one of the
          note's own fee clauses. See the card's own header. */}
      <BuyerConsultingFeeTermsCard loan={loan} />
      {/* Its own card, and deliberately neither folded into the one above nor
          into LoanTermsCard's note-fee block: this fee is charged to the
          BORROWER (the card above is a carve out of the seller), and it has no
          staff write path at all where that block is editable under
          Loans.Manage. It also carries a THIRD state the card above does not —
          a null meaning "no such term" rather than 0%. See its own header. */}
      <PaymentAdministrationFeeTermsCard loan={loan} />

      {!profile && history.length === 0 ? (
        <Card>
          <div className="font-display font-bold text-h3 text-onyx mb-space-1">
            No broker intake data
          </div>
          <p className="text-small text-gray-mid m-0">
            This Loan has no carried-forward intake profile — it pre-dates the
            broker submission carry-forward.
          </p>
        </Card>
      ) : (
        <>
          <FinancialHistoryCard
            loanId={loan.id}
            rows={history}
            caption={dealCaption}
            canEdit={canUpdate}
          />
          {profile &&
            INTAKE_SECTIONS.map((section) => (
              <IntakeSectionCard
                key={section.id}
                profile={profile}
                sectionId={section.id}
                caption={dealCaption}
                canEdit={canUpdate}
                onEdit={() => setEditingSection(section.id)}
              />
            ))}
        </>
      )}

      <CovenantCalibrationCard loanId={loan.id} />
      <SeasonalityReserveCard loanId={loan.id} />
      <DscrDacaBankingCard loanId={loan.id} />

      <LoanAdminFieldsCard loanId={loan.id} />

      {editingSection && profile && (
        <IntakeSectionEditDrawer
          // Remount per open so the drawer re-hydrates from fresh data.
          key={editingSection}
          loanId={loan.id}
          profile={profile}
          sectionId={editingSection}
          open
          onClose={() => setEditingSection(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Carried intake sections (display driven by the shared section config)
// ---------------------------------------------------------------------------

function displayValue(field: IntakeFieldConfig, value: IntakeFieldValue): string {
  if (value === null || value === undefined || value === "") return "—";
  switch (field.type) {
    case "money":
      return typeof value === "number" ? formatCurrency(value) : String(value);
    case "yesno":
      return value === true ? "Yes" : value === false ? "No" : "—";
    case "select": {
      const match = field.options?.find((o) => o.value === value);
      return match?.label ?? String(value);
    }
    default:
      return String(value);
  }
}

function IntakeSectionCard({
  profile,
  sectionId,
  caption,
  canEdit,
  onEdit,
}: {
  profile: LoanIntakeProfileDto;
  sectionId: IntakeSectionId;
  caption: React.ReactNode;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const section = INTAKE_SECTIONS.find((s) => s.id === sectionId)!;
  const values = sectionValues(profile, sectionId);
  const editedFields = profile.editedFields ?? [];

  return (
    <Card data-testid={`intake-section-${section.id}`}>
      <div className="mb-space-3 flex items-start justify-between gap-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">
            {section.title}
          </div>
          <p className="text-caption text-gray-mid m-0">{caption}</p>
        </div>
        {canEdit && (
          <Button
            variant="tertiary"
            size="sm"
            onClick={onEdit}
            data-testid={`intake-section-edit-${section.id}`}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 text-small">
        {section.fields.map((f) => (
          <div key={f.key} className={f.wide ? "md:col-span-2" : undefined}>
            <div className="font-display text-caption uppercase tracking-label text-gray-mid">
              {f.label}
            </div>
            <div className="text-onyx mt-[2px] whitespace-pre-wrap">
              {displayValue(f, values[f.key])}
            </div>
            {editedFields.includes(f.key) && (
              <div
                className="text-caption text-gray-mid italic"
                data-testid={`intake-edited-${f.key}`}
              >
                Edited post-close
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
